import React, { useCallback, useEffect, useState } from "react";
import { useCSVDownloader } from "react-papaparse";

import html2canvas from "html2canvas";
import * as html2pdf from "html2pdf.js";
import * as JSZip from "jszip";

import { getAllPendingPayments, processPayments } from "services";

import { formatNumericCurrency, saveFile, useCancelAsyncOnUnmount, useTranslation } from "utils";

import { AdminHead, AdminTable, Button, Col, Icon, MainPage, Modal, Row, Tooltip, Message as message } from "components";

let zip = new JSZip();

export const PendingPaymentList = () => {
  const { t } = useTranslation("points");

  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [paymentsWithMissingInformation, setPaymentsWithMissingInformation] = useState([]);
  const signal = useCancelAsyncOnUnmount()();
  const [selectedIds, setSelectedIds] = useState([]);
  const { CSVDownloader } = useCSVDownloader();

  const [currentPdfIndex, setCurrentPdfIndex] = useState(0);
  const [pdfs, setPdfs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showModalConfirm, setShowModalConfirm] = useState(false);

  // If none is selected in the table we want to pay all farms
  const selectedPayments = selectedIds.length === 0 ? payments : payments.filter(({ id }) => selectedIds.includes(id));

  const loadAllPendingPayments = useCallback(() => {
    getAllPendingPayments({
      signal,
    })
      .then((res) => {
        setPayments(res.withBankingDetails);
        setPaymentsWithMissingInformation(res.withoutBankingDetails);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [signal]);

  useEffect(() => {
    loadAllPendingPayments();
  }, [loadAllPendingPayments]);

  const addPayments = () => {
    setSaving(true);

    processPayments({
      // We only need to send farmId and payment
      payments: selectedPayments.map(({ farmId, payment }) => ({ farmId, payment })),
      signal,
    }).then(({ processedCount }) => {
      loadAllPendingPayments();
      setSaving(false);
      setShowModalConfirm(false);

      message({
        content: `The gremlins have successfully processed ${processedCount} payments.`,
        icon: "gremlin",
      });
    });
    // NOTE: I commented this out because it led to a bug (see https://bitbucket.org/klim-app/client/pull-requests/2491/diff)
    // TODO: if invoices are not going to be used we need to clean up the component
    // .then((pdfs) => setPdfs(pdfs));
  };

  // This is a bit hacky. What we do here is kinda recursively create pdfs one by one
  // using html2pdf library and adding them to a zip. Once every pdf is created we download the zip
  const generateZip = useCallback(async () => {
    const container = document.getElementById("pdfContainer");

    // If every pdf is generated we download the zip
    if (currentPdfIndex > 0 && currentPdfIndex === pdfs.length) {
      setPdfs([]);
      setCurrentPdfIndex(0);
      setSaving(false);
      setShowModalConfirm(false);

      const blob = await zip.generateAsync({ type: "blob" });
      saveFile(blob, "invoices.zip");

      // Reset zip
      zip = new JSZip();
    }

    if (pdfs[currentPdfIndex] && container) {
      const footers = container.getElementsByClassName("pdf-foot");
      const footerCanvas = await html2canvas(footers[0]);
      const footerImage = footerCanvas.toDataURL();
      const footerClone = footers[0].cloneNode(true);
      footers[0].remove();

      const filename = `${pdfs[currentPdfIndex].note}.pdf`;

      const opt = {
        margin: [0, 0, 50, 0],
        filename,
        image: {
          type: "jpeg",
          quality: 0.98,
        },
        jsPDF: {
          unit: "mm",
          orientation: "portrait",
          format: "a4",
        },
        pagebreak: { before: ".break-page" },
      };

      html2pdf()
        .from(container)
        .set(opt)
        .toPdf()
        .get("pdf")
        .then((pdf) => {
          // This is needed for a correct footer
          const totalPages = pdf.internal.getNumberOfPages();
          for (let i = 1; i <= totalPages; i += 1) {
            pdf.setPage(i);
            pdf.addImage(footerImage, "PNG", 1, 297 - 35, 208, 34);
          }
          container.appendChild(footerClone);
        })
        .outputPdf()
        .then((pdf) => {
          zip.file(filename, pdf, { binary: true });
          setCurrentPdfIndex((prev) => prev + 1);
        });
    }
  }, [currentPdfIndex, pdfs]);

  useEffect(() => {
    generateZip();
  }, [generateZip]);

  return (
    <MainPage loading={loading} pageName={"admin pdfs"}>
      {t("bankingReference")}
      <AdminHead
        headline={`${payments.length + paymentsWithMissingInformation.length} pending payment records`}
        description={
          <>
            Select the farms you want to trigger a payout for and then click on the button on the right. If you want to pay out all farms don't select any. Be
            aware that this will not only generate the csv, but also notify the farmers of the payout. Be patient as generating all the invoices might take a
            while.
          </>
        }
        extra={
          <div className="right">
            <Tooltip placement="bottom" title="Generate a payment csv">
              <Button size="small" type="secondary" disabled={saving || payments.length === 0} onClick={() => setShowModalConfirm(true)}>
                <Icon iconName="tabler/wallet" size="s" />
              </Button>
            </Tooltip>
          </div>
        }
      />
      <Row>
        <Col>
          <h3>{payments.length} Pending payment(s) ready to process</h3>
        </Col>
        <Col>
          <AdminTable
            dataSource={payments.map(({ payment, ...rest }) => ({ ...rest, "payment incl vat": formatNumericCurrency(payment) }))}
            onRowSelectionChange={(ids) => setSelectedIds(ids)}
            rowKey={"id"}
            config={{
              exclude: ["bankingDetails", "id"],
              actions: [
                {
                  icon: "tabler/eye",
                  route: "/farm/",
                  record: "farmId",
                  tooltip: "View this farm",
                },
              ],
            }}
          />
        </Col>
      </Row>

      {paymentsWithMissingInformation.length > 0 && (
        <Row>
          <Col>
            <h3>{paymentsWithMissingInformation.length} Pending payment(s) with missing banking information</h3>
            <p>The following payouts have been approved but are missing banking information needed to complete a transaction.</p>
          </Col>
          <Col>
            <AdminTable
              dataSource={paymentsWithMissingInformation}
              rowKey={"id"}
              config={{
                exclude: ["bankingDetails", "id"],
                actions: [
                  {
                    icon: "tabler/eye",
                    route: "/farm/",
                    record: "farmId",
                    tooltip: "View this farm",
                  },
                ],
              }}
            />
          </Col>
        </Row>
      )}

      {pdfs.length > 0 && (
        <Row>
          <Col>
            <div id="pdfContainer" className="data" dangerouslySetInnerHTML={{ __html: pdfs[currentPdfIndex]?.data || "" }} />
          </Col>
        </Row>
      )}

      <CSVDownloader
        filename={"payments"}
        delimiter={","}
        data={selectedPayments.map(({ payment, bankingDetails }) => ({
          beneficiary_name: bankingDetails?.accountHolder,
          iban: bankingDetails?.iban,
          amount: payment,
          currency: "EUR",
          reference: t("bankingReference"),
        }))}
      >
        <Modal
          cancelText="close"
          description={`You have selected ${selectedPayments.length} ${
            selectedPayments.length > 1 ? "farms" : "farm"
          } to pay out. Are you sure you want to proceed? This will trigger notifications to those farms and mark them as paid in the database.`}
          onOk={addPayments}
          onCancel={(e) => {
            setShowModalConfirm(false);
            // Use stop propagation to now trigger csv download
            e.stopPropagation();
          }}
          okText={"proceed"}
          showModal={showModalConfirm}
          title={"Pay out farms"}
          loading={saving}
        />
      </CSVDownloader>
    </MainPage>
  );
};
